import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  findGameMatch,
  inviteFriendGame,
  listGames,
  startAgentGame,
  stopGameMatch,
  type GameAgentPick,
  type GameSide,
  type GameType,
  type GameView,
  type MahjongView,
} from '../chatv1/transport'
import { onChatv1Frame, onChatv1Open } from '../chatv1/ws'
import { Icon } from '../ui/Icon'
import { Avatar } from '../chat/Avatar'
import { getFriends } from '../transport/api'
import { AgentPick } from './AgentPick'
import { gameErrorText, outcomeText, seatName } from './gameText'
import { openGame } from './gameNav'
import { kindOf, listedKinds, type GameKind } from './gameKinds'
import { MahjongLobby } from './MahjongLobby'
import { mahjongOn } from './flag'
import { knownKind, rememberKind } from './gameNav'
import './games.css'

/**
 * ＋ → GAME — owner, 2026-10-19: 新增一個遊戲的欄位, 使用這個點進去之後可以打開他想要玩的遊戲. 開發的第一款遊戲是西洋棋,
 * 讓兩個使用者可以互相對弈. 使用者如果找不到其他玩家配對的話, 也可以跟他自己的 Agent 來配對來下棋.
 *
 * The games (gameKinds.ts: Chess, then Gomoku — 之後再做五子棋 — with xiangqi, shogi and mahjong to follow, one at a
 * time), and for a game: invite a friend, find a player, play one of your agents, or go back to a game you are in.
 * Opening a game closes the ＋ sheet; the game takes the screen.
 */

/**
 * How long a wait stays up — the service's own ticket backstop (games.TICKET_TTL_S). Nothing re-asks meanwhile: the
 * service pairs only people whose app is open, and tells this one by a frame the moment it does.
 */
const WAIT_MS = 600_000

type View = 'games' | 'lobby' | 'finding' | 'agent' | 'friend' | 'mahjong'

export function GamesSection({ onOpened, onBack }: { onOpened: () => void; onBack?: () => void }) {
  const [view, setView] = useState<View>('games')
  const [kind, setKind] = useState<GameKind>(() => kindOf('chess'))
  const open = (id: string) => {
    // Anything opened from here that is not known as a table is a board game (the mahjong lobby says which it made).
    if (!knownKind(id)) rememberKind(id, 'board')
    openGame(id)
    onOpened()
  }
  // ONE back row per screen, naming where it goes: the ＋ menu from the list, the list from a game, the game from
  // what it opened.
  const back = (to: View | 'menu', label: string) => (
    <button type="button" className="ac-back" aria-label="Back" onClick={() => (to === 'menu' ? onBack?.() : setView(to))}>
      <Icon name="chevron_left" size={18} />
      {label}
    </button>
  )
  if (view === 'games') {
    return (
      <>
        {onBack && back('menu', 'Game')}
        <div className="ac-menu">
          {listedKinds().map((k) => (
            <button
              key={k.type}
              type="button"
              className="ac-row"
              onClick={() => {
                setKind(k)
                setView('lobby')
              }}
            >
              <Icon name={k.icon} size={18} className="lp-ink-peach" />
              <span className="ac-row-label">{k.title}</span>
              <span className="ac-row-val">{k.sub}</span>
              <Icon name="chevron_right" size={18} className="ac-row-go" />
            </button>
          ))}
          {/* MAHJONG (owner, 2026-10-26: 開始做麻將) — four seats, so its own lobby (MahjongLobby). */}
          {mahjongOn() && (
            <button type="button" className="ac-row" onClick={() => setView('mahjong')}>
              <Icon name="style" size={18} className="lp-ink-peach" />
              <span className="ac-row-label">Mahjong</span>
              <span className="ac-row-val">Taiwan 16-tile · 4 seats</span>
              <Icon name="chevron_right" size={18} className="ac-row-go" />
            </button>
          )}
        </div>
        <YourGames onOpen={open} />
      </>
    )
  }
  if (view === 'mahjong') {
    return (
      <>
        <MahjongLobby
          onOpen={(id) => {
            rememberKind(id, 'mahjong')
            open(id)
          }}
          onBack={() => setView('games')}
        />
        <YourGames type="mahjong" onOpen={open} />
      </>
    )
  }
  if (view === 'finding') {
    return (
      <>
        {back('lobby', kind.title)}
        <FindPlayer type={kind.type} onMatched={open} onAgentInstead={() => setView('agent')} onCancel={() => setView('lobby')} />
      </>
    )
  }
  if (view === 'agent') {
    return (
      <>
        {back('lobby', kind.title)}
        <PlayAgent kind={kind} onStarted={open} />
      </>
    )
  }
  if (view === 'friend') {
    return (
      <>
        {back('lobby', kind.title)}
        <InviteFriend kind={kind} onInvited={open} />
      </>
    )
  }
  return (
    <>
      {back('games', 'Games')}
      <div className="gm-lobby">
        <button type="button" className="gm-big" onClick={() => setView('friend')}>
          <Icon name="group" size={22} />
          <span>
            <strong>Invite a friend</strong>
            <span className="gm-big-sub">Send one of your friends a game — they accept, and you play.</span>
          </span>
        </button>
        <button type="button" className="gm-big" onClick={() => setView('finding')}>
          <Icon name="groups" size={22} />
          <span>
            <strong>Find a player</strong>
            <span className="gm-big-sub">Play someone else on Avengents who is looking for a game.</span>
          </span>
        </button>
        <button type="button" className="gm-big" onClick={() => setView('agent')}>
          <Icon name="smart_toy" size={22} />
          <span>
            <strong>Play your agent</strong>
            <span className="gm-big-sub">One of your own agents takes the other side.</span>
          </span>
        </button>
      </div>
      <YourGames type={kind.type} onOpen={open} />
    </>
  )
}

/** Waiting for a player: asked once (and again only when the socket comes back), paired the moment someone else asks. */
function FindPlayer({
  type,
  onMatched,
  onAgentInstead,
  onCancel,
}: {
  type: GameType
  onMatched: (id: string) => void
  onAgentInstead: () => void
  onCancel: () => void
}) {
  const [err, setErr] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  const done = useRef(false)
  // The wait runs ONCE per visit: a new callback from a re-rendered parent must not restart it (and take the ticket
  // down in between).
  const onMatchedRef = useRef(onMatched)
  onMatchedRef.current = onMatched
  useEffect(() => {
    let alive = true
    const matched = (id: string) => {
      if (done.current) return
      done.current = true
      onMatchedRef.current(id)
    }
    const ask = async () => {
      try {
        const r = await findGameMatch(type)
        if (alive && r.status === 'matched') matched(r.game.id)
      } catch (e) {
        if (alive) setErr(gameErrorText(e))
      }
    }
    void ask()
    // A frame can only have been missed while the socket was away: ask again when it is back.
    const offOpen = onChatv1Open(() => void ask())
    // A DEADLINE, not a poll: when the service's ticket would lapse, say so instead of spinning on.
    const deadline = setTimeout(() => {
      if (alive && !done.current) {
        setExpired(true)
        void stopGameMatch(type).catch(() => {})
      }
    }, WAIT_MS)
    // Paired by someone else's ask: the service tells this socket at once.
    const off = onChatv1Frame((f) => {
      if (
        f.type === 'game' &&
        f.game.type === type &&
        f.game.mode === 'match' &&
        f.game.status === 'active' &&
        f.game.moves.length === 0
      ) {
        matched(f.game.id)
      }
    })
    return () => {
      alive = false
      clearTimeout(deadline)
      offOpen()
      off()
      // Leaving the wait takes the ticket down — nobody should be paired with a person who walked away.
      if (!done.current) void stopGameMatch(type).catch(() => {})
    }
  }, [type])
  return (
    <div className="gm-finding">
      {expired ? (
        <p className="gm-finding-say" role="status">
          Nobody else was looking just now.
        </p>
      ) : (
        <>
          <div className="gm-spinner" aria-hidden="true" />
          <p className="gm-finding-say" role="status">
            Looking for a player…
          </p>
          <p className="gh-note">
            You will be paired with the next person who looks for a game while this stays open. Each of you sees the
            other’s name and picture.
          </p>
        </>
      )}
      {err && <p className="gh-note mch-err">{err}</p>}
      <div className="gm-row">
        <button type="button" className="fr-btn" onClick={onCancel}>
          Stop looking
        </button>
        <button type="button" className="fr-btn ok" onClick={onAgentInstead}>
          Play your agent instead
        </button>
      </div>
    </div>
  )
}

function PlayAgent({ kind, onStarted }: { kind: GameKind; onStarted: (id: string) => void }) {
  const [agent, setAgent] = useState<GameAgentPick | null>(null)
  // You move first unless you choose otherwise.
  const [side, setSide] = useState<GameSide | 'random'>(kind.first)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const start = async () => {
    if (!agent) return
    setBusy(true)
    setErr(null)
    try {
      const r = await startAgentGame(kind.type, agent, side === 'random' ? undefined : side)
      onStarted(r.game.id)
    } catch (e) {
      setErr(gameErrorText(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="gm-form">
      <div className="nas-sec">Your agent</div>
      <AgentPick picked={agent} onPick={setAgent} />
      <div className="nas-sec">You play</div>
      <SidePick kind={kind} side={side} onSide={setSide} />
      <p className="gh-note">Your agent is sent the board on its turn and answers with its move — it may take a little while.</p>
      {err && <p className="gh-note mch-err">{err}</p>}
      <div className="svr-createrow svr-creatego">
        <button type="button" className="sheet-btn primary" disabled={!agent || busy} onClick={() => void start()}>
          Start game
        </button>
      </div>
    </div>
  )
}

/**
 * A FRIEND, DIRECTLY — owner, 2026-10-19: 使用者可以直接對他的好友送出對弈的邀請. Your friends, each by name and picture;
 * the game is made at once and waits for their answer.
 */
function InviteFriend({ kind, onInvited }: { kind: GameKind; onInvited: (id: string) => void }) {
  const friends = useQuery({ queryKey: ['friends'], queryFn: getFriends })
  // The chosen friend by ACCOUNT when the list has it: two friends at one address are two rows (2026-10-31).
  const [picked, setPicked] = useState<string | null>(null)
  const [side, setSide] = useState<GameSide | 'random'>('random')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const rows = friends.data?.friends ?? []
  const idOf = (f: { email: string; account?: string }) => f.account ?? f.email
  const chosen = rows.find((f) => idOf(f) === picked) ?? null
  const send = async () => {
    if (!chosen) return
    setBusy(true)
    setErr(null)
    try {
      const r = await inviteFriendGame(kind.type, chosen.email, side === 'random' ? undefined : side, chosen.account)
      onInvited(r.game.id)
    } catch (e) {
      setErr(gameErrorText(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="gm-form">
      <div className="nas-sec">Your friend</div>
      {friends.isPending && <p className="gh-note">Loading your friends…</p>}
      {friends.isError && <p className="gh-note mch-err">Your friends could not be loaded — try again.</p>}
      {friends.isSuccess && !rows.length && <p className="gh-note">No friends yet. Add one in Humans, then come back.</p>}
      {rows.length > 0 && (
        <div className="ac-menu gm-agents" role="radiogroup" aria-label="Your friends">
          {rows.map((f) => {
            const on = picked === idOf(f)
            return (
              <button
                key={idOf(f)}
                type="button"
                role="radio"
                aria-checked={on}
                className={`ac-row${on ? ' on' : ''}`}
                onClick={() => setPicked(idOf(f))}
              >
                <Avatar picture={f.picture} label={f.name || f.email} email={f.email} className="gm-face gm-face-sm" />
                <span className="ac-row-label">{f.name || f.email}</span>
                {on && <Icon name="check" size={18} className="ac-row-go" />}
              </button>
            )
          })}
        </div>
      )}
      <div className="nas-sec">You play</div>
      <SidePick kind={kind} side={side} onSide={setSide} />
      {err && <p className="gh-note mch-err">{err}</p>}
      <div className="svr-createrow svr-creatego">
        <button type="button" className="sheet-btn primary" disabled={!chosen || busy} onClick={() => void send()}>
          Send invitation
        </button>
      </div>
    </div>
  )
}

/** Your colour — the side that moves first listed first (White in chess, Black in gomoku, Red in xiangqi). */
function SidePick({
  kind,
  side,
  onSide,
}: {
  kind: GameKind
  side: GameSide | 'random'
  onSide: (s: GameSide | 'random') => void
}) {
  const first = kind.first
  const second: GameSide = first === 'w' ? 'b' : 'w'
  return (
    <div className="vm-seg" role="radiogroup" aria-label="You play">
      {(
        [
          [first, `${kind.sides[first]} (moves first)`],
          [second, kind.sides[second]],
          ['random', 'Random'],
        ] as Array<[GameSide | 'random', string]>
      ).map(([v, label]) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={side === v}
          className={`vm-seg-btn${side === v ? ' on' : ''}`}
          onClick={() => onSide(v)}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

function gameLine(g: GameView): string {
  if (g.status === 'over') return outcomeText(g)
  if (g.status === 'invited') return g.invited === 'them' ? 'Invites you' : 'Invitation sent'
  return g.turn === g.you ? 'Your move' : 'Their move'
}

/** A table's line in the list: an invitation, whose turn, or how it ended for you. */
function tableLine(t: MahjongView): string {
  const me = t.you === null ? null : t.seats[t.you]
  if (t.status === 'invited') return me?.invite === 'pending' ? 'Invites you' : 'Invitation sent'
  if (t.status === 'active' && me?.left) return 'You left'
  if (t.status === 'over') {
    const d = (me?.chips ?? t.settings.chips) - t.settings.chips
    return d > 0 ? `Over · +${d.toLocaleString('en-US')}` : d < 0 ? `Over · −${(-d).toLocaleString('en-US')}` : 'Over · even'
  }
  return t.phase?.yours ? 'Your turn' : `Deal ${t.round.hand}`
}

/** Your games — of every kind on the games list, of one kind in that game's lobby. */
function YourGames({ type, onOpen }: { type?: GameType | 'mahjong'; onOpen: (id: string) => void }) {
  const q = useQuery({ queryKey: ['games'], queryFn: async () => await listGames() })
  const games = (q.data?.games ?? []).filter((g) => !type || g.type === type)
  const tables = mahjongOn() && (!type || type === 'mahjong') ? (q.data?.mahjong ?? []) : []
  if (q.isPending || (!games.length && !tables.length)) return null
  for (const t of tables) rememberKind(t.id, 'mahjong')
  // Invitations waiting on you first, then games being played, then the last ten finished.
  const invites = games.filter((g) => g.status === 'invited').sort((a, b) => (a.invited === 'them' ? -1 : 0) - (b.invited === 'them' ? -1 : 0))
  const active = [...invites, ...games.filter((g) => g.status === 'active')]
  const done = games.filter((g) => g.status === 'over').slice(0, 10)
  return (
    <>
      <div className="gh-sec grp-h">Your games</div>
      <div className="ac-menu">
        {[...active, ...done].map((g) => {
          const them: GameSide = g.you === 'w' ? 'b' : 'w'
          const k = kindOf(g.type)
          return (
            <button key={g.id} type="button" className="ac-row" onClick={() => onOpen(g.id)}>
              <Icon name={k.icon} size={18} className={g.status === 'active' ? 'lp-ink-peach' : ''} />
              <span className="ac-row-label">
                {seatName(g, them)}
                {g.players[them].kind === 'agent' ? ' (your agent)' : ''}
              </span>
              <span
                // Green for what waits on you: your move, or an invitation to answer — never a finished game.
                className={`ac-row-val${(g.status === 'active' && g.turn === g.you) || (g.status === 'invited' && g.invited === 'them') ? ' ac-ok' : ''}`}
              >
                {/* Every kind together on the games list: each row says which game it is. */}
                {type ? gameLine(g) : `${k.title} · ${gameLine(g)}`}
              </span>
              <Icon name="chevron_right" size={18} className="ac-row-go" />
            </button>
          )
        })}
        {tables
          .filter((t) => t.status !== 'over')
          .concat(tables.filter((t) => t.status === 'over').slice(0, 10))
          .map((t) => {
            const others = t.seats.filter((s) => !s.you && s.kind !== 'house').map((s) => s.name)
            const waiting = (t.status === 'active' && !!t.phase?.yours) || (t.status === 'invited' && t.you !== null && t.seats[t.you]?.invite === 'pending')
            return (
              <button key={t.id} type="button" className="ac-row" onClick={() => onOpen(t.id)}>
                <Icon name="style" size={18} className={t.status === 'active' ? 'lp-ink-peach' : ''} />
                <span className="ac-row-label">{others.length ? others.join(', ') : 'The computer'}</span>
                <span className={`ac-row-val${waiting ? ' ac-ok' : ''}`}>{type ? tableLine(t) : `Mahjong · ${tableLine(t)}`}</span>
                <Icon name="chevron_right" size={18} className="ac-row-go" />
              </button>
            )
          })}
      </div>
    </>
  )
}
